import isEqual from "lodash.isequal";
import * as React from "react";
import {useState} from "react";

import {useIsMounted} from "@khanacademy/wonder-blocks-core";
import {useMutation} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";

import {SaveChangesButton} from "../save-changes-button.tsx";
import type {PlaybackRate} from "../types/account-settings-types.ts";

import {SET_VIDEO_SETTINGS_MUTATION} from "./set-video-settings-mutation.ts";
import StudentVideoSettings from "./student-settings-video.tsx";

interface VideoSettingsData {
    muteVideos: boolean;
    showCaptions: boolean;
    playbackRate: PlaybackRate;
}

interface Props {
    kaid: string;
    initialSettings: VideoSettingsData;
    successfullyChangedSettings: () => unknown;
}

export const VideoSettings: React.FC<Props> = ({
    kaid,
    initialSettings,
    successfullyChangedSettings,
}) => {
    const [currentSettings, setCurrentSettings] =
        useState<VideoSettingsData>(initialSettings);
    const [isUnexpectedError, setIsUnexpectedError] = useState(false);
    const isMounted = useIsMounted();

    const [setVideoSettingsMutation, mutationResult] = useMutation(
        SET_VIDEO_SETTINGS_MUTATION,
    );

    const onSettingsChange = (newSettings: Partial<VideoSettingsData>) => {
        setIsUnexpectedError(false);
        setCurrentSettings((prev) => ({
            ...prev,
            ...newSettings,
        }));
    };

    const setSettings = async (settings: VideoSettingsData) => {
        const {muteVideos, showCaptions, playbackRate} = settings;

        setIsUnexpectedError(false);

        try {
            const result = await setVideoSettingsMutation({
                kaid,
                muteVideos,
                showCaptions,
                playbackRate,
            });

            if (!isMounted()) {
                return;
            }

            if (result.status === "success") {
                const errors = result.data?.setSettings?.errors;
                if (errors && errors[0]) {
                    throw new KAError(errors[0].code, Errors.Internal);
                }

                successfullyChangedSettings();
            } else if (result.status === "error") {
                throw new KAError(
                    "Failed to set video settings",
                    Errors.Internal,
                    {
                        cause: result.error,
                    },
                );
            }
        } catch (error) {
            if (!isMounted()) {
                return;
            }

            setIsUnexpectedError(true);
        }
    };

    const settings = {
        ...initialSettings,
        ...currentSettings,
    };

    const isSaveChangesDisabled =
        mutationResult?.status === "loading" ||
        isEqual(initialSettings, settings);

    const {muteVideos, showCaptions, playbackRate} = currentSettings;

    return (
        <StudentVideoSettings
            muteVideos={muteVideos}
            showCaptions={showCaptions}
            playbackRate={playbackRate}
            disabled={false}
            onChangeMuteVideos={(muteVideos) => {
                onSettingsChange({muteVideos});
            }}
            onChangeShowCaptions={(showCaptions) => {
                onSettingsChange({showCaptions});
            }}
            onChangePlaybackRate={(playbackRate) => {
                onSettingsChange({playbackRate});
            }}
            footer={
                <SaveChangesButton
                    setSettings={() => setSettings(settings)}
                    isLoading={mutationResult?.status === "loading"}
                    isDisabled={isSaveChangesDisabled}
                    hasUnexpectedError={isUnexpectedError}
                />
            }
        />
    );
};

export default React.memo(VideoSettings);
